// Mock data for WiFi Access Points based on real AP locations in Cary
// Performance metrics are determined by location type, crowd patterns, and network load

// Location types and their typical usage patterns
const LOCATION_TYPES = {
  ACADEMY_PAVILION: { // Event/gathering space - high usage during events
    baseLoad: 0.6,
    peakHours: [10, 11, 12, 18, 19, 20], // Morning events, lunch, evening events
    weekendMultiplier: 1.8,
    maxDevices: 80
  },
  DOWNTOWN_PARK_OFFICE: { // Administrative area - business hours usage
    baseLoad: 0.4,
    peakHours: [9, 10, 11, 13, 14, 15], // Business hours
    weekendMultiplier: 0.3,
    maxDevices: 30
  },
  GATHERING_HOUSE: { // Community center - moderate consistent usage
    baseLoad: 0.5,
    peakHours: [9, 12, 15, 18], // Program times
    weekendMultiplier: 1.2,
    maxDevices: 50
  },
  OUTDOOR_PARK: { // General park area - variable usage based on weather/events
    baseLoad: 0.3,
    peakHours: [12, 13, 17, 18], // Lunch and evening recreation
    weekendMultiplier: 2.0,
    maxDevices: 60
  },
  GROUND_LEVEL: { // Ground installations - consistent moderate usage
    baseLoad: 0.4,
    peakHours: [8, 12, 17], // Commute and lunch times
    weekendMultiplier: 1.1,
    maxDevices: 40
  },
  LOWER_LEVEL: { // Underground/covered areas - lower but steady usage
    baseLoad: 0.2,
    peakHours: [9, 12, 15], // Limited peak times
    weekendMultiplier: 0.8,
    maxDevices: 25
  }
};

// Helper function to determine location type from AP location string
const getLocationType = (locationStr) => {
  const location = locationStr.toLowerCase();
  if (location.includes('academy pavilion')) return LOCATION_TYPES.ACADEMY_PAVILION;
  if (location.includes('downtown park office')) return LOCATION_TYPES.DOWNTOWN_PARK_OFFICE;
  if (location.includes('gathering house')) return LOCATION_TYPES.GATHERING_HOUSE;
  if (location.includes('lower level')) return LOCATION_TYPES.LOWER_LEVEL;
  if (location.includes('ground')) return LOCATION_TYPES.GROUND_LEVEL;
  return LOCATION_TYPES.OUTDOOR_PARK; // Default for outdoor park areas
};

// Helper function to calculate network metrics based on time and location
const calculateNetworkMetrics = (locationType, dayOffset = 0, hourOffset = 0, isWeekend = false, seed = 0) => {
  const currentHour = (new Date().getHours() + hourOffset) % 24;
  
  let loadFactor = locationType.baseLoad;
  
  // Add peak hour bonus
  if (locationType.peakHours.includes(currentHour)) {
    loadFactor += 0.3;
  }
  
  // Weekend multiplier
  if (isWeekend) {
    loadFactor *= locationType.weekendMultiplier;
  }
  
  // Add deterministic "randomness" based on seed
  const pseudoRandom1 = (Math.sin(seed * 1234) + 1) / 2;
  const pseudoRandom2 = (Math.sin(seed * 5678) + 1) / 2;
  const pseudoRandom3 = (Math.sin(seed * 9012) + 1) / 2;
  
  loadFactor += (pseudoRandom1 - 0.5) * 0.4; // Add some variation
  loadFactor = Math.max(0.05, Math.min(1.0, loadFactor)); // Clamp between 5% and 100%
  
  // Calculate connected devices
  let connectedDevices = Math.round(locationType.maxDevices * loadFactor);
  
  // Calculate signal strength (affected by load and environmental factors)
  let signalStrength = 95 - (loadFactor * 20) + (pseudoRandom2 - 0.5) * 15;
  signalStrength = Math.max(30, Math.min(100, Math.round(signalStrength)));
  
  // Calculate bandwidth utilization
  let bandwidthUtil = loadFactor * 85 + (pseudoRandom3 - 0.5) * 20;
  bandwidthUtil = Math.max(5, Math.min(95, Math.round(bandwidthUtil)));
  
  // Determine health status
  let health = 'Excellent';
  let status = 'Active';
  
  if (signalStrength < 50 || bandwidthUtil > 90) {
    health = 'Poor';
  } else if (signalStrength < 70 || bandwidthUtil > 80) {
    health = 'Fair';
  } else if (signalStrength < 85 || bandwidthUtil > 70) {
    health = 'Good';
  }
  
  // Simulate occasional outages
  if (pseudoRandom1 < 0.02) { // 2% chance of being inactive
    status = 'Inactive';
    connectedDevices = 0;
    signalStrength = 0;
    bandwidthUtil = 0;
    health = 'Poor';
  }
  
  return {
    connectedDevices,
    signalStrength,
    bandwidthUtil,
    health,
    status,
    loadFactor
  };
};

// Helper function to generate time series data for network performance
const generateNetworkTimeSeries = (locationType, seed = 0) => {
  const timeSeries = [];
  const now = new Date();
  
  // Generate 24 hours of data (hourly intervals)
  for (let i = 23; i >= 0; i--) {
    const date = new Date(now);
    date.setHours(date.getHours() - i);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    
    const metrics = calculateNetworkMetrics(locationType, 0, -i, isWeekend, seed + i);
    
    timeSeries.push({
      time: i === 0 ? 'Now' : `${i}h ago`,
      timestamp: date.toISOString(),
      connectedDevices: metrics.connectedDevices,
      signalStrength: metrics.signalStrength,
      bandwidthUtil: metrics.bandwidthUtil,
      hour: date.getHours()
    });
  }
  
  return timeSeries;
};

// Generate mock data for all WiFi access points
export const generateWifiMockData = () => {
  const apDefinitions = [
    { ap_id: "AP-002", lat: 35.791708, lng: -78.780147, ssids: "CaryOn_FreePublicWifi, DCP Production, ToC_Staff", location: "1, DTP, Outdoor, Park" },
    { ap_id: "AP-003", lat: 35.7917224, lng: -78.7803095, ssids: "CaryOn_FreePublicWifi, DCP Production, DTP_Vendor, ToC_Staff, ToC_public", location: "Academy Pavilion, DTP, Downtown Park Office, Ground, Lower Level, Outdoor, Park" },
    { ap_id: "AP-004", lat: 35.7917536, lng: -78.7805147, ssids: "CaryOn_FreePublicWifi, DCP Production, ToC_Staff, ToC_public", location: "Academy Pavilion, DTP, Downtown Park Office, Outdoor, Park" },
    { ap_id: "AP-005", lat: 35.792034, lng: -78.7793953, ssids: "CaryOn_FreePublicWifi, DCP Production, ToC_Staff, ToC_public", location: "1, Academy Pavilion, DTP, Lower Level, Outdoor, Park" },
    { ap_id: "AP-006", lat: 35.7920345, lng: -78.7813483, ssids: "CaryOn_FreePublicWifi, DCP Production, ToC_Staff", location: "1, Academy Pavilion, DTP, Lower Level, Outdoor, Park" },
    { ap_id: "AP-007", lat: 35.7920943, lng: -78.7805409, ssids: "CaryOn_FreePublicWifi, DCP Production, ToC_Staff, ToC_public", location: "1, DTP, Outdoor, Park" },
    { ap_id: "AP-008", lat: 35.792159, lng: -78.78104, ssids: "CaryOn_FreePublicWifi, ToC_public", location: "DTP, Downtown Park Office, Ground, Outdoor, Park" },
    { ap_id: "AP-009", lat: 35.792161, lng: -78.7789009, ssids: "CaryOn_FreePublicWifi, DCP Production, ToC_Staff", location: "DTP, Ground, Outdoor, Park" },
    { ap_id: "AP-010", lat: 35.7921619, lng: -78.7803642, ssids: "CaryOn_FreePublicWifi, DTP_Vendor, ToC_Staff, ToC_public", location: "1, DTP, Ground, Outdoor, Park" },
    { ap_id: "AP-011", lat: 35.7921731, lng: -78.7812103, ssids: "CaryOn_FreePublicWifi, DCP Production, DTP_Vendor, ToC_public", location: "1, DTP, Ground, Outdoor, Park" },
    { ap_id: "AP-012", lat: 35.7921937, lng: -78.780023, ssids: "CaryOn_FreePublicWifi, DCP Production, ToC_Staff, ToC_public", location: "1, DTP, Downtown Park Office, Gathering House, Outdoor, Park" },
    { ap_id: "AP-013", lat: 35.7922054, lng: -78.7799738, ssids: "CaryOn_FreePublicWifi, DCP Production, ToC_Staff, ToC_public", location: "1, Academy Pavilion, DTP, Ground, Outdoor" },
    { ap_id: "AP-014", lat: 35.7922316, lng: -78.7800367, ssids: "CaryOn_FreePublicWifi, DCP Production, ToC_Staff", location: "Academy Pavilion, DTP, Downtown Park Office, Outdoor, Park" },
    { ap_id: "AP-015", lat: 35.7922462, lng: -78.779987, ssids: "CaryOn_FreePublicWifi, ToC_Staff, ToC_public", location: "1, DTP, Outdoor, Park" },
    { ap_id: "AP-016", lat: 35.7922647, lng: -78.7811235, ssids: "CaryOn_FreePublicWifi, DCP Production, DTP_Vendor, ToC_Staff, ToC_public", location: "1, Academy Pavilion, DTP, Downtown Park Office, Ground, Lower Level, Outdoor, Park" },
    { ap_id: "AP-017", lat: 35.7922897, lng: -78.7811571, ssids: "CaryOn_FreePublicWifi, DCP Production, DTP_Vendor, ToC_Staff", location: "1, Academy Pavilion, DTP, Lower Level, Outdoor, Park" },
    { ap_id: "AP-018", lat: 35.7923359, lng: -78.7810618, ssids: "CaryOn_FreePublicWifi, ToC_Staff, ToC_public", location: "DTP, Downtown Park Office, Outdoor, Park" },
    { ap_id: "AP-019", lat: 35.7924002, lng: -78.7801516, ssids: "CaryOn_FreePublicWifi, DCP Production, ToC_Staff, ToC_public", location: "1, Academy Pavilion, DTP, Outdoor, Park" },
    { ap_id: "AP-020", lat: 35.7924004, lng: -78.7801631, ssids: "CaryOn_FreePublicWifi, ToC_Staff, ToC_public", location: "DTP, Downtown Park Office, Outdoor, Park" },
    { ap_id: "AP-021", lat: 35.7924104, lng: -78.7807421, ssids: "CaryOn_FreePublicWifi, DCP Production, ToC_Staff", location: "DTP, Ground, Lower Level, Outdoor, Park" },
    { ap_id: "AP-022", lat: 35.7924577, lng: -78.7812208, ssids: "CaryOn_FreePublicWifi, DCP Production, DTP_Vendor, ToC_Staff, ToC_public", location: "1, DTP, Downtown Park Office, Lower Level, Outdoor, Park" },
    { ap_id: "AP-023", lat: 35.7925033, lng: -78.7789499, ssids: "CaryOn_FreePublicWifi, DCP Production, DTP_Vendor, ToC_Staff", location: "Academy Pavilion, DTP, Ground, Outdoor, Park" },
    { ap_id: "AP-024", lat: 35.7925196, lng: -78.7800358, ssids: "CaryOn_FreePublicWifi, DTP_Vendor, ToC_Staff, ToC_public", location: "Academy Pavilion, DTP, Downtown Park Office, Outdoor, Park" },
    { ap_id: "AP-025", lat: 35.7925566, lng: -78.7801091, ssids: "CaryOn_FreePublicWifi, DCP Production, DTP_Vendor, ToC_Staff, ToC_public", location: "1, Academy Pavilion, DTP, Ground, Outdoor, Park" },
    { ap_id: "AP-026", lat: 35.7926364, lng: -78.7801432, ssids: "CaryOn_FreePublicWifi, DTP_Vendor, ToC_Staff, ToC_public", location: "DTP, Downtown Park Office, Ground, Lower Level, Outdoor, Park" },
    { ap_id: "AP-027", lat: 35.7926467, lng: -78.7800678, ssids: "CaryOn_FreePublicWifi, DCP Production, ToC_Staff", location: "DTP, Outdoor, Park" },
    { ap_id: "AP-028", lat: 35.7926648, lng: -78.7799711, ssids: "CaryOn_FreePublicWifi, DCP Production, ToC_Staff, ToC_public", location: "DTP, Downtown Park Office, Outdoor, Park" },
    { ap_id: "AP-029", lat: 35.7926672, lng: -78.7803676, ssids: "CaryOn_FreePublicWifi, DCP Production, DTP_Vendor, ToC_Staff, ToC_public", location: "Academy Pavilion, DTP, Downtown Park Office, Outdoor, Park" },
    { ap_id: "AP-030", lat: 35.7927058, lng: -78.7800679, ssids: "CaryOn_FreePublicWifi, DCP Production, ToC_Staff", location: "1, DTP, Outdoor, Park" },
    { ap_id: "AP-031", lat: 35.7927059, lng: -78.7799811, ssids: "CaryOn_FreePublicWifi, DCP Production, DTP_Vendor, ToC_Staff", location: "Academy Pavilion, DTP, Ground, Outdoor, Park" },
    { ap_id: "AP-032", lat: 35.792928, lng: -78.7805359, ssids: "CaryOn_FreePublicWifi, DTP_Vendor, ToC_Staff, ToC_public", location: "DTP, Downtown Park Office, Ground, Outdoor, Park" },
    { ap_id: "AP-033", lat: 35.7929365, lng: -78.7791061, ssids: "CaryOn_FreePublicWifi, DCP Production, ToC_Staff", location: "Academy Pavilion, DTP, Ground, Lower Level, Outdoor, Park" },
    { ap_id: "AP-034", lat: 35.7929414, lng: -78.7802905, ssids: "CaryOn_FreePublicWifi, DCP Production, ToC_Staff, ToC_public", location: "1, DTP, Downtown Park Office, Outdoor, Park" },
    { ap_id: "AP-035", lat: 35.7929539, lng: -78.7803786, ssids: "CaryOn_FreePublicWifi, DCP Production, DTP_Vendor, ToC_Staff", location: "Academy Pavilion, DTP, Lower Level, Outdoor, Park" },
    { ap_id: "AP-036", lat: 35.7929642, lng: -78.7792649, ssids: "CaryOn_FreePublicWifi, DCP Production, ToC_Staff, ToC_public", location: "DTP, Downtown Park Office, Lower Level, Outdoor, Park" },
    { ap_id: "AP-037", lat: 35.7929824, lng: -78.7805091, ssids: "CaryOn_FreePublicWifi, DCP Production, ToC_Staff", location: "DTP, Ground, Outdoor, Park" },
    { ap_id: "AP-038", lat: 35.7929999, lng: -78.7803802, ssids: "CaryOn_FreePublicWifi, DCP Production, ToC_Staff, ToC_public", location: "1, DTP, Downtown Park Office, Ground, Lower Level, Outdoor, Park" },
    { ap_id: "AP-039", lat: 35.7930176, lng: -78.7805362, ssids: "CaryOn_FreePublicWifi, DTP_Vendor, ToC_Staff, ToC_public", location: "1, Academy Pavilion, DTP, Outdoor, Park" },
    { ap_id: "AP-040", lat: 35.7930182, lng: -78.7791013, ssids: "CaryOn_FreePublicWifi, DCP Production, DTP_Vendor, ToC_Staff", location: "1, DTP, Outdoor, Park" },
    { ap_id: "AP-041", lat: 35.79306, lng: -78.779243, ssids: "CaryOn_FreePublicWifi, DCP Production, ToC_Staff", location: "DTP, Downtown Park Office, Ground, Outdoor, Park" },
    { ap_id: "AP-042", lat: 35.79308, lng: -78.779443, ssids: "CaryOn_FreePublicWifi, DCP Production, ToC_Staff", location: "DTP, Downtown Park Office, Outdoor, Park" },
    { ap_id: "AP-043", lat: 35.7933473, lng: -78.7790386, ssids: "CaryOn_FreePublicWifi, DCP Production, DTP_Vendor, ToC_Staff", location: "Academy Pavilion, DTP, Outdoor, Park" },
    { ap_id: "AP-044", lat: 35.7933789, lng: -78.7799113, ssids: "CaryOn_FreePublicWifi, DCP Production, ToC_Staff, ToC_public", location: "DTP, Downtown Park Office, Outdoor, Park" }
  ];

  const accessPoints = apDefinitions.map((apDef, index) => {
    const locationType = getLocationType(apDef.location);
    const seed = index * 456; // Use index as seed for consistent data
    const currentMetrics = calculateNetworkMetrics(locationType, 0, 0, false, seed);
    
    // Generate additional deterministic values
    const pseudoRandom1 = (Math.sin(seed * 1111) + 1) / 2;
    const pseudoRandom2 = (Math.sin(seed * 2222) + 1) / 2;
    const pseudoRandom3 = (Math.sin(seed * 3333) + 1) / 2;
    
    // Parse SSIDs
    const ssidList = apDef.ssids.split(', ').map(ssid => ssid.trim());
    
    // Calculate uptime and last update
    const uptimeHours = Math.floor(pseudoRandom1 * 168) + 24; // 24-192 hours uptime
    const lastUpdateMinutes = Math.floor(pseudoRandom2 * 30); // 0-30 minutes ago
    const lastUpdate = new Date(Date.now() - lastUpdateMinutes * 60 * 1000);
    
    return {
      id: apDef.ap_id,
      name: apDef.ap_id,
      location: apDef.location,
      coordinates: {
        latitude: apDef.lat,
        longitude: apDef.lng
      },
      status: currentMetrics.status,
      health: currentMetrics.health,
      lastUpdate: lastUpdate.toISOString(),
      ssids: ssidList,
      primarySSID: ssidList[0],
      metrics: {
        connectedDevices: currentMetrics.connectedDevices,
        signalStrength: currentMetrics.signalStrength,
        bandwidthUtilization: currentMetrics.bandwidthUtil,
        maxDevices: locationType.maxDevices,
        uptime: uptimeHours,
        dataTransferred: Math.round(pseudoRandom3 * 1000) + 100, // MB transferred today
        errorRate: Math.round((1 - currentMetrics.loadFactor) * 5), // Lower load = lower error rate
        temperature: Math.round(pseudoRandom1 * 20) + 20, // 20-40°C
        powerConsumption: Math.round(15 + currentMetrics.loadFactor * 10) // 15-25W based on load
      },
      locationType: Object.keys(LOCATION_TYPES).find(key => LOCATION_TYPES[key] === locationType),
      timeSeries24h: generateNetworkTimeSeries(locationType, seed),
      // Network security and configuration
      security: {
        encryption: 'WPA3-Enterprise',
        authMethod: 'EAP-TLS',
        lastSecurityScan: new Date(Date.now() - Math.floor(pseudoRandom2 * 7 * 24 * 60 * 60 * 1000)).toISOString(),
        vulnerabilities: pseudoRandom3 < 0.1 ? ['Outdated firmware'] : []
      }
    };
  });

  return {
    accessPoints: accessPoints,
    summary: {
      totalAPs: accessPoints.length,
      activeAPs: accessPoints.filter(ap => ap.status === 'Active').length,
      inactiveAPs: accessPoints.filter(ap => ap.status === 'Inactive').length,
      totalConnectedDevices: accessPoints.reduce((sum, ap) => sum + ap.metrics.connectedDevices, 0),
      averageSignalStrength: Math.round(accessPoints.reduce((sum, ap) => sum + ap.metrics.signalStrength, 0) / accessPoints.length),
      averageBandwidthUtil: Math.round(accessPoints.reduce((sum, ap) => sum + ap.metrics.bandwidthUtilization, 0) / accessPoints.length),
      healthDistribution: {
        excellent: accessPoints.filter(ap => ap.health === 'Excellent').length,
        good: accessPoints.filter(ap => ap.health === 'Good').length,
        fair: accessPoints.filter(ap => ap.health === 'Fair').length,
        poor: accessPoints.filter(ap => ap.health === 'Poor').length
      },
      lastUpdated: new Date().toISOString()
    }
  };
};

// Get AP data by ID or name
export const getAPByName = (apName) => {
  const allData = generateWifiMockData();
  return allData.accessPoints.find(ap => ap.name === apName || ap.id === apName);
};

// Get APs by location type
export const getAPsByLocation = (locationType) => {
  const allData = generateWifiMockData();
  return allData.accessPoints.filter(ap => ap.locationType === locationType);
};

// Get APs needing attention
export const getAPsNeedingAttention = () => {
  const allData = generateWifiMockData();
  return allData.accessPoints.filter(ap => 
    ap.status === 'Inactive' || 
    ap.health === 'Poor' || 
    ap.metrics.signalStrength < 50 ||
    ap.metrics.bandwidthUtilization > 90
  );
};

// Export default data for immediate use
export default generateWifiMockData();
